/**
 * The user list, with inline role change and disable.
 *
 * TWO CONTROLS ARE WITHHELD ON YOUR OWN ROW, and both prevent a lockout that is awkward to undo:
 *
 *   - Demoting yourself immediately loses you this screen, and only a manager can restore it.
 *   - Disabling yourself revokes the account AND makes it unlistable (see `RestoreUserField`),
 *     so the way back is a Frappe admin or a Postman call.
 *
 * Both are UI-level only. The server neither knows nor cares that you are editing yourself, so
 * these prevent an obvious mistake — they are not a security boundary.
 *
 * Matching is on `currentUserEmail`, the Frappe `User` docname from `useSession().user`, never a
 * display name: a lookup keyed on a name that happens to match is the single most-repeated bug
 * this codebase has found (CLAUDE.md, "API conventions").
 *
 * ONE SHARED MUTATION, ONE IN-FLIGHT UPDATE AT A TIME — deliberately, not an oversight.
 * `update` below is a single `useMutation()` for the whole table, so its `isPending`/`error`/
 * `variables` are one slot, not one per row. Rather than give each row its own mutation (or track
 * an in-flight row's email in local state) to unfreeze the rest of the table while one row saves,
 * every role `<select>` and both "Disable" buttons are gated on the SAME `update.isPending` — the
 * whole table briefly freezes on any edit. That is what buys the one-slot model its correctness
 * for free: because a second edit can never start before the first settles, `update.error` can
 * never belong to the wrong row, and `update.variables` (the last-submitted input) unambiguously
 * names who a failure was about. Per-row liveness would need to reinvent that attribution — via
 * per-call `mutate(vars, { onError })` callbacks and its own local state — to buy back something
 * this table (a purchase manager reviewing a handful of accounts) barely notices: edits are
 * sub-second, and "everything else is briefly disabled" reads as normal saving-in-progress
 * feedback, not as a bug.
 */

import { useState } from 'react'
import { Button } from '../../../components/ui'
import { ROLE_LABELS } from '../../../components/SessionProvider'
import { useUpdateUser, type BosunUser } from '../../../lib/api/useUsers'
import { ROLES, type Role } from '../../../lib/types'

export function UserTable({
  users,
  currentUserEmail,
}: {
  users: BosunUser[]
  currentUserEmail: string
}) {
  const update = useUpdateUser()
  const [confirming, setConfirming] = useState<string | null>(null)

  // Attributable because only one mutation is ever in flight (see header comment): `variables` is
  // always the input that just failed, never a stale or unrelated row's.
  const failedUser = update.isError ? users.find((u) => u.email === update.variables?.email) : undefined

  const errorBanner = update.isError && (
    <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>
      Could not update {failedUser ? failedUser.fullName || failedUser.email : 'that user'}:{' '}
      {update.error instanceof Error ? update.error.message : 'Something went wrong. Please try again.'}
    </div>
  )

  if (users.length === 0) {
    return (
      <div>
        {errorBanner}
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>No users yet.</div>
      </div>
    )
  }

  return (
    <div>
      {errorBanner}
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th style={{ width: 1 }} />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isSelf = u.email === currentUserEmail
            return (
              <tr key={u.email}>
                <td>
                  <div>{u.fullName || u.email}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>{u.email}</div>
                </td>
                <td>
                  {isSelf ? (
                    <span title="You cannot change your own role — you would lose this screen.">
                      {u.role ? ROLE_LABELS[u.role] : 'No Waves role'} <span style={{ color: 'var(--muted)' }}>(you)</span>
                    </span>
                  ) : (
                    <select
                      value={u.role ?? ''}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ email: u.email, role: e.target.value as Role })}
                    >
                      {u.role === null && <option value="">No Waves role</option>}
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  )}
                  {/* Shown for every row, not only when `role` is null (finding 3 of the
                      2026-08-09 whole-branch review): `role` REPLACES a user's Frappe roles
                      wholesale rather than adding to them (spec §3.2), so nudging this dropdown
                      on a colleague who also holds e.g. `System Manager` or `Accounts Manager`
                      silently revokes that ERP access. Surfacing the raw list on every row makes
                      what is about to be replaced visible before it happens, not just after. Kept
                      visually quiet — it is context for the change, not the row's main content. */}
                  <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 4 }}>
                    Frappe roles: {u.frappeRoles.join(', ') || '—'}
                  </div>
                </td>
                <td>
                  {isSelf ? null : confirming === u.email ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 12 }}>
                        Disable <b>{u.fullName || u.email}</b>? Waves cannot list them afterwards.
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={update.isPending}
                        onClick={() => {
                          update.mutate({ email: u.email, enabled: false })
                          setConfirming(null)
                        }}
                      >
                        Disable
                      </Button>
                      <button type="button" className="linkish" onClick={() => setConfirming(null)}>
                        cancel
                      </button>
                    </div>
                  ) : (
                    <Button size="sm" disabled={update.isPending} onClick={() => setConfirming(u.email)}>
                      Disable
                    </Button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
